import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotificationPreferences } from '@ngsos/portal-core';
import { NotificationSettings, ThemeProvider } from '@ngsos/portal-ui';

import { webPushRegistrar } from '@/push/webPushRegistrar';
import { NgCheckbox } from '@/ui';

import { INCIDENT_NOTIFICATION_TYPE, incidentNotificationFlags } from './pushNotificationTypes';
import type { PushNotificationConfiguration } from './pushNotificationTypes';

export type PushSubscriptionProps = {
  configuration: PushNotificationConfiguration;
};

function readControlColor(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--ng-primary-color')
    .trim();
  return value.length > 0 ? value : undefined;
}

export function PushSubscription({ configuration }: PushSubscriptionProps) {
  const { t } = useTranslation();
  const { status, loading, busy, toggleEnabled, toggleCritical } =
    useNotificationPreferences(webPushRegistrar);
  const [controlColor] = useState(readControlColor);
  const [showDetail, setShowDetail] = useState(false);

  const labels = {
    title: t('Settings_Title_PushNotifications'),
    description: t('Settings_PushNotifications_BrowserPermissions'),
    enableLabel: t('Settings_PushNotifications_AllowPushNotifications'),
    unsupported: t('Settings_PushNotifications_NotAvailableOnDevice'),
    criticalLabel: t('Settings_PushNotifications_Critical', {
      defaultValue: 'Critical notifications',
    }),
    criticalDescription: t('Settings_PushNotifications_CriticalDescription', {
      defaultValue: 'Critical alerts break through silent mode and Do-Not-Disturb.',
    }),
  };

  const incidentNotificationType = incidentNotificationFlags(configuration);
  const hasFlag = (flag: number) => (incidentNotificationType & flag) === flag;

  const notificationOptions = [
    { text: t('Settings_PushNotifications_NewIncident'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.NewIncident) },
    { text: t('Settings_PushNotifications_IncidentUpdatePosition'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentPositionChanged) },
    { text: t('Settings_PushNotifications_IncidentReplacedPosition'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentPositionReplaced) },
    { text: t('Settings_PushNotifications_IncidentDataChanged'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentDataChanged) },
    { text: t('Settings_PushNotifications_IncidentClosed'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentClosed) },
    { text: t('Settings_PushNotifications_IncidentCollaborationRequested'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentCollaborationRequested) },
    { text: t('Settings_PushNotifications_IncidentCollaborationConfirmed'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentCollaborationConfirmed) },
    { text: t('Settings_PushNotifications_IncidentCollaborationRejected'), isChecked: hasFlag(INCIDENT_NOTIFICATION_TYPE.IncidentCollaborationRejected) },
  ];

  const configuredTypes = (
    <>
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-none"
        onClick={() => setShowDetail((value) => !value)}
      >
        <span className={showDetail ? 'fas fa-chevron-up me-2' : 'fas fa-chevron-down me-2'} />
        {t('Settings_PushNotifications_ConfiguredTypes', { defaultValue: 'Configured notifications' })}
      </button>

      <div className={showDetail ? 'd-block pe-2 mt-2' : 'd-none'}>
        {notificationOptions.map((item) => (
          <NgCheckbox
            key={item.text}
            text={item.text}
            value={item.isChecked}
            onValueChanged={() => {}}
            enabled={false}
          />
        ))}
      </div>
    </>
  );

  return (
    <ThemeProvider primaryColor={controlColor}>
      <NotificationSettings
        supported={status?.supported ?? false}
        enabled={status?.enabled ?? false}
        busy={loading || busy}
        onToggleEnabled={(next) => void toggleEnabled(next)}
        criticalSupported={status?.criticalSupported ?? false}
        criticalEnabled={status?.criticalEnabled ?? false}
        onToggleCritical={(next) => void toggleCritical(next)}
        labels={labels}
        footer={configuredTypes}
      />
    </ThemeProvider>
  );
}
